import { Variation } from '../redux/types';

export interface DaysAvailability {
  [day: string]: {
    state: DayState;
    status: DayStatus;  // toDo: make api changes to return partially selected from state, not status
  };
}

export enum DayStatus {
  PartiallyBooked = 'partiallyBooked',
}

export interface DateRangeStr {
  from: string;
  to: string;
}

export interface HourRange {
  from: string;
  to: string;
}

export enum ButtonIcon {
  Add = 'add',
  Cart = 'cart',
  Date = 'date',
  Location = 'location',
}

export enum CartStatus {
  Active = 'active',
  Empty = 'empty',
  Expired = 'expired',
}

export interface Price {
  available?: boolean;
  gross: number;
  net: number;
  vat?: number;
  mileageOptions?: MileageOption[];
  quantity?: {
    min: number;
    max: number;
    available: number;
  };
  priceCalculation?: PriceCalculation;
}

export interface ShortPrice {
  gross: number;
  net: number;
  priceCalculation?: PriceCalculation;
}

export interface AmountToPay {
  total: number;
  withSubtractedGiftCard: number;
}

export interface MileageOption {
  id: string;
  amount: number;
  relativeAmount: RelativeAmount;
  price: Price;
  relativePrice: RelativePrice;
  config: {
    amount: number;
    includedAmount: number;
    price: ShortPrice;
    period: MileagePeriod;
    unit: string;
  };
}

export interface RelativeAmount {
  amount: number;
  calculationType: PriceCalculation;
}

export interface RelativePrice {
  price: ShortPrice;
  calculationType: PriceCalculation;
}

export interface AddToCartParams {
  itemId: string;
  amountToAdd?: number;
  dateRange?: DateRangeStr;
  fullDay?: boolean;
  variationsIds?: string[];
  mileageOptionId?: string;
  locationId?: string;
  people?: number;
  timeSlot?: string;
  price?: ShortPrice;
  name?: string;
}

export interface DayConfig {
  date: Date;
  dayState: DayState;
  isToday: boolean;
  inPast: boolean;
  selected: boolean;
  isPickupDate: boolean;
  isDropOffDate: boolean;
  isInRange: boolean;
  isDropHoverOffDate: boolean;
  inRangeLeftRound: boolean;
  inRangeRightRound: boolean;
  isInHoverRange: boolean;
  inHoverRangeLeftRound: boolean;
  inHoverRangeRightRound: boolean;
  clickToReset: boolean;
  isAfterOrOnPickup: boolean;
}

export enum DayState {
  Selectable = 'selectable',
  SelectablePartially = 'selectablePartially',
  Through = 'through',
  Blocked = 'blocked',
}

export interface ShortArticle {
  id: string;
  key?: string; // id as key is not enough in some cases
  orderItemId: string;
  name: string;
  title: string;
  description: string;
  image: string;
  images: string[];

  available: boolean;
  quantityInCart: number;
  minQuantity: number;
  maxQuantity: number;
  price: ShortPrice;

  mileageOptions?: MileageOption[];
  mileageIdInCart: string;
  variation: Variation;

  //related to state, calculated mostly by engine, not from api
  selectedMileageId?: string;
  selectedVariationId?: string;
  hasAvailableVariation?: boolean; // used to determine if article will be available after auto switch to available variation
  hasSelectedVariationInCart?: boolean;
  calculatedQuantity?: number;
  calculatedMaxQuantity?: number;
  isWaiting?: boolean;
  lastArticle?: boolean;
}

export enum SplitOptionType {
  Percentage = 'percentage',
  Amount = 'amount',
  Deposit = 'deposit',
}

export interface AmountControlValues {
  disabled?: boolean;
  itemAmount?: number;
  minAmount?: number;
  maxAmount?: number;
  isVariation?: boolean;
  text?: string;
  changeAmountCallback?: (newValue: number, oldValue: number) => void;
  deleteArticleCallback?: () => void;
}

export enum ComponentView {
  Booking = 'booking',
  BookingCTA = 'bookingCTA',
  Calendar = 'calendar',
  Cards = 'cards',
  Cart = 'cart',
  Checkout = 'checkout',
  Config = 'config',
  NewConfig = 'newConfig',
  Default = 'default',
  Details = 'details',
  Item = 'item',
  List = 'list',
  Search = 'search',
  Slots = 'slots',
  Order = 'order',
}

export enum WidgetType {
  Default = 'default',
  FixedDuration = 'fixed-duration',
  FixedHours = 'fixed-hours',
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export enum EnabledDisabled {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
}

export enum BookingButtonStatus {
  Active = 'Active',
  Disabled = 'Disabled',
}

export enum ButtonSimpleStyles {
  Default = 'Default',
  Warn = 'Warn',
  Filter = 'Filter',
  Config = 'Config',
  Backend = 'Backend',
}

export enum VoucherStatus {
  Progress = 'Progress',
  Done = 'Done',
}

export enum LoadBehaviour {
  Extended = 'extended',
  Collapsed = 'collapsed',
  LinkTo = 'link-to',
}

export enum Filters {
  Tags = 'Tags',
  Sort = 'Sort',
}

export enum CartIcon {
  AirShip = 'AirShip',
  Camera = 'Camera',
  Bike = 'Bike',
  Wagon = 'Wagon',
  Boat = 'Boat',
  Box = 'Box',
  Calendar = 'Calendar',
  Car = 'Car',
  Id = 'Id',
  Kayak = 'Kayak',
  Racer = 'Racer',
  Rv = 'RV',
  Scooter = 'Scooter',
  Tent = 'Tent',
  Ticket = 'Ticket',
  Tool = 'Tool',
  Trailer = 'Trailer',
  Trey = 'Trey',
  Trolley = 'Trolley',
  Truck = 'Truck',
  Yacht = 'Yacht',
}

export enum StepErrorType {
  ShippingNoOptionSelected = 'shippingNoOptionSelected',
  ShippingDistanceProblem = 'shippingDistanceProblem',
  PaymentNoOptionSelected = 'paymentNoOptionSelected',
  PaymentNotAcceptedTerms = 'paymentNotAcceptedTerms',
  PaymentProblemWithMethod = 'paymentProblemWithMethod',
  PaymentProblemWithPasscode = 'PaymentProblemWithPasscode',
  PaymentFailed = 'paymentFailed',
  BillingNotAllFieldsValid = 'billingNotAllFieldsValid',
  StepInvalid = 'stepInvalid',
  ValidationStepMinQuantityError = 'validationStepMinQuantityError',
  ValidationStepMaxQuantityError = 'validationStepMaxQuantityError',
  SecondaryPaymentNoOptionSelected = 'secondaryPaymentNoOptionSelected',
  FormNotAllFieldsValid = 'formNotAllFieldsValid',
}

export enum PriceCalculation {
  Interval = 'interval',
  Day = 'day',
  CalendarDay = 'calendarDay',
  Hour = 'hour',
  Night = 'night',
  Month = 'month',
}

export enum TotalPriceCalculation {
  Interval = 'interval',
  Month = 'month',
}

export enum MileagePeriod {
  calendarDay = 'calendar day',
  day = 'day',
  hour = 'hour',
  total = 'total',
}

export enum OnOff {
  On = 'on',
  Off = 'off',
}

export enum SortByOptions {
  Default = 'default',
  Price = 'price',
  Name = 'name',
}

export enum SortDirOptions {
  Asc = 'asc',
  Desc = 'desc',
}

export enum SelectType {
  Single = 'Single',
  Multi = 'Multi',
}

export enum SelectStyle {
  Default = 'Default',
  Simple = 'Simple',
  Search = 'Search',
  Variations = 'Variations',
  Config = 'Config',
  NewConfig = 'NewConfig',
}

export enum InputStyle {
  Default = 'Default',
  DefaultWithFileUpload = 'DefaultWithFileUpload',
  Config = 'Config',
  Simple = 'Simple',
  NewConfig = 'NewConfig',
  OrderConfig = 'OrderConfig',
}

export interface SelectOption {
  value: string;
  label: string;
  labelPrice?: string;
  labelSecondRow?: string;
  customIconName?: string;
  customIconStyle?: string;
  disabled?: boolean;
}

export interface MenuTab {
  id: string;
  name: string;
  iconUrl?: string;
}

export class Color {
  red: number;
  green: number;
  blue: number;
  alpha: number;

  constructor();
  constructor(color: string);
  constructor(red: number, green: number, blue: number);
  constructor(redOrColor: number | string = 0, green: number = 0, blue: number = 0) {
    if (typeof redOrColor === 'number') {
      this.red = redOrColor;
      this.green = green;
      this.blue = blue;
    } else if (typeof redOrColor === 'string') {
      if (redOrColor[0] == '#') {
        this.fromHexString(redOrColor);
      } else {
        const colorsArray = redOrColor.split(',');
        this.red = +colorsArray[0];
        this.green = +colorsArray[1];
        this.blue = +colorsArray[2];
      }
    }
  }

  get RGBString(): string {
    return this.red + ', ' + this.green + ', ' + this.blue + (this.isAlpha ? ', ' + this.alpha : '');
  }

  get RGBArray(): [number, number, number, number?] {
    return [this.red, this.green, this.blue, this.alpha];
  }

  calculateSecondaryBackgroundRGB(): string {
    const colorArray = this.intArray.map(part => {
      if (this.average / 255 < 0.5) {
        return part + 10;
      } else {
        return part - 10;
      }
    });

    return colorArray.join(',');
  }

  calculateTertiaryBackgroundRGB(): string {
    const colorArray = this.intArray.map(part => {
      if (this.average / 255 < 0.5) {
        return part + 20;
      } else {
        return part - 20;
      }
    });

    return colorArray.join(',');
  }

  calculateQuaternaryBackgroundRGB(): string {
    const colorArray = this.intArray.map(part => {
      if (this.average / 255 < 0.5) {
        return part + 30;
      } else {
        return part - 30;
      }
    });

    return colorArray.join(',');
  }

  calculateShadowRGB(): string {
    const shadowParameter = 15;

    const shadowAverage = 255 - this.average;

    const shadowArray = this.intArray.map(() => {
      if (shadowAverage / 255 < 0.5) {
        return shadowAverage + shadowParameter;
      } else {
        return shadowAverage - shadowParameter;
      }
    });

    return shadowArray.join(',');
  }

  calculateSecondaryRGB(backgroundColor: Color): string {
    const parameter = 0.2;

    const calculatedArray = this.intArray.map((part, index) => {
      const partBg = backgroundColor.RGBArray[index];
      const diff = (partBg - part) * parameter;

      return Math.floor(part + diff);
    });

    return calculatedArray.join(',');
  }

  calculateTertiaryRGB(backgroundColor: Color): string {
    const parameter = 0.4;

    const calculatedArray = this.intArray.map((part, index) => {
      const partBg = backgroundColor.RGBArray[index];
      const diff = (partBg - part) * parameter;

      return Math.floor(part + diff);
    });

    return calculatedArray.join(',');
  }

  get intArray() {
    if (this.isAlpha) {
      return [this.red, this.green, this.blue, this.alpha];
    }

    return [this.red, this.green, this.blue];
  }

  get average() {
    return Math.floor((this.red + this.green + this.blue) / 3);
  }

  get isAlpha() {
    return this.alpha !== undefined && this.alpha !== null;
  }

  get HexString() {
    const redHex = this.red.toString(16).padStart(2, '0').toUpperCase();
    const greenHex = this.green.toString(16).padStart(2, '0').toUpperCase();
    const blueHex = this.blue.toString(16).padStart(2, '0').toUpperCase();
    const alphaHex = this.isAlpha ? this.alpha.toString(16).padStart(2, '0').toUpperCase() : '';
    return '#' + alphaHex + redHex + greenHex + blueHex;
  }

  fromRGBString(color) {
    const colorArray = color.split(',');
    if (colorArray.length === 3) {
      [this.red, this.green, this.blue] = colorArray;
    } else if (colorArray.length === 4) {
      [this.red, this.green, this.blue, this.alpha] = colorArray;
    }
  }

  fromHexString(color) {
    //  we check here if we use hex color with alpha (9 length) or without (7 length)
    //  we do not use shortened notation, like #fff
    if (color.length === 7) {
      const hexArray = color.match(/[A-Za-z0-9]{2}/g);
      [this.red, this.green, this.blue] = hexArray.map(v => parseInt(v, 16));
    } else if (color.length === 9) {
      const hexArray = color.match(/[A-Za-z0-9]{2}/g);
      [this.alpha, this.red, this.green, this.blue] = hexArray.map(v => parseInt(v, 16));
      this.alpha /= 255;
    }
  }
}
